// src/pages/qc/PageQCDailyAnalysis.jsx
import { useState, useEffect, useMemo } from "react";
import * as api from "../../apiService";
import { DailyAnalysisModal } from "./DailyAnalysisModal";
import { MonthYearSelect } from "../../components/MonthYearSelect";
import { currentMonthKey } from "../../utils/dateUtils";
import { useToast } from "../../components/Toast";

function fieldVerdict(value, min, max) {
  if (value == null) return "SKIP";
  if (min == null && max == null) return "SKIP";
  const v = parseFloat(value);
  if ((min != null && v < parseFloat(min)) || (max != null && v > parseFloat(max))) return "FAIL";
  return "PASS";
}

function viscVerdict(row, spec) {
  if (row.viscosity == null || !spec) return "SKIP";
  const unit = (row.viscUnit || "").toUpperCase();
  if (unit === "SEC" || unit === "S")
    return fieldVerdict(row.viscosity, spec.b4CupMin, spec.b4CupMax);
  return fieldVerdict(row.viscosity, spec.brookfieldMin, spec.brookfieldMax);
}

function rowVerdict(row, spec) {
  if (!spec) return "NS";
  const verdicts = [
    fieldVerdict(row.ph, spec?.phMin, spec?.phMax),
    fieldVerdict(row.solidPct, spec?.solidContentMin, spec?.solidContentMax),
    fieldVerdict(row.density, spec?.densityMin, spec?.densityMax),
    viscVerdict(row, spec),
  ].filter(v => v !== "SKIP");
  if (verdicts.length === 0) return "NS";
  if (verdicts.includes("FAIL")) return "FAIL";
  return "PASS";
}

function VerdictBadge({ verdict }) {
  const cfg = {
    PASS: { bg:"#d1fae5", color:"#065f46", border:"#6ee7b7", label:"PASS" },
    FAIL: { bg:"#fee2e2", color:"#991b1b", border:"#fca5a5", label:"FAIL" },
    NS:   { bg:"#fef9c3", color:"#854d0e", border:"#fde047", label:"N/S"  },
  };
  const c = cfg[verdict] ?? cfg.NS;
  return (
    <span style={{ background:c.bg, color:c.color, border:`1px solid ${c.border}`,
      borderRadius:4, padding:"2px 8px", fontSize:11, fontWeight:600 }}>
      {c.label}
    </span>
  );
}

const FAIL_CELL = { background:"#fee2e2", color:"#991b1b", fontWeight:600 };
const ROW_BG    = { PASS:"#f0fdf4", FAIL:"#fff1f2", NS:"#fefce8" };

function FailCell({ verdict, children, ...rest }) {
  return <td style={verdict === "FAIL" ? FAIL_CELL : {}} {...rest}>{children}</td>;
}

export function PageQCDailyAnalysis({ fgs, sfgs, myPerms }) {
  const showToast = useToast();
  const [rows,    setRows]    = useState([]);
  const [specs,   setSpecs]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,    setSearch]    = useState("");
  const [monthKey,  setMonthKey]  = useState(currentMonthKey);
  const [modal,     setModal]     = useState(false);

  const canDelete = myPerms?.includes("USER_MANAGE");

  useEffect(() => {
    Promise.all([api.fetchDailyAnalysis(), api.fetchFgSpecs()])
      .then(([analyses, fgSpecs]) => { setRows(analyses); setSpecs(fgSpecs); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  const specMap = useMemo(() => {
    const m = {};
    specs.forEach(s => { m[s.fgId] = s; });
    return m;
  }, [specs]);

  const allProducts = useMemo(() => [...(fgs||[]), ...(sfgs||[])], [fgs, sfgs]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(r =>
      (r.date || "").startsWith(monthKey) &&
      ((r.productId || "").toLowerCase().includes(q) ||
       (r.batchNo   || "").toLowerCase().includes(q))
    );
  }, [rows, search, monthKey]);

  async function handleCreate(body) {
    const created = await api.createDailyAnalysis(body);
    setRows(old => [created, ...old]);
    showToast("Analysis saved");
    setModal(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this analysis record?")) return;
    await api.deleteDailyAnalysis(id);
    setRows(old => old.filter(r => r.id !== id));
    showToast("Record deleted", "info");
  }

  function productName(id) {
    if (!id) return "—";
    const p = allProducts.find(f => f.id === id);
    return p ? `${id} — ${p.name}` : id;
  }

  const colCount = canDelete ? 10 : 9;

  return (
    <>
      <div className="page-header">
        <div className="page-header-left">
          <div className="page-title">Water Based Daily Analysis</div>
          <div className="page-subtitle">Pass / Fail verdict against FG specifications</div>
        </div>
        <div className="page-header-right">
          <button className="btn btn-primary" onClick={() => setModal(true)}>+ Add Analysis</button>
        </div>
      </div>

      <div className="action-bar">
        <MonthYearSelect value={monthKey} onChange={setMonthKey} />
        <input className="form-input" placeholder="Search product or batch…" value={search}
          onChange={e => setSearch(e.target.value)} style={{ width:240 }} />
      </div>

      {loading && <div style={{ color:"#aaa", fontSize:12 }}>Loading…</div>}
      {error   && <div className="alert-warn">Error: {error}</div>}

      {!loading && !error && (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Production Date</th>
                <th>Product</th>
                <th>Batch No</th>
                <th>Viscosity</th>
                <th>pH</th>
                <th>Density</th>
                <th>Solid %</th>
                <th>Remarks</th>
                <th>Verdict</th>
                {canDelete && <th></th>}
              </tr>
            </thead>
            <tbody>
              {filtered.map(row => {
                const spec   = specMap[row.productId];
                const verdict = rowVerdict(row, spec);
                const bg      = ROW_BG[verdict] ?? "#fff";
                const phV     = fieldVerdict(row.ph, spec?.phMin, spec?.phMax);
                const denV    = fieldVerdict(row.density, spec?.densityMin, spec?.densityMax);
                const scV     = fieldVerdict(row.solidPct, spec?.solidContentMin, spec?.solidContentMax);
                const viscV   = viscVerdict(row, spec);
                return (
                  <tr key={row.id} style={{ background: bg }}>
                    <td style={{ whiteSpace:"nowrap" }}>{row.date}</td>
                    <td className="fw-600" style={{ whiteSpace:"nowrap" }}>{productName(row.productId)}</td>
                    <td>{row.batchNo || "—"}</td>
                    <FailCell verdict={viscV}>
                      {row.viscosity != null
                        ? <>{row.viscosity} {row.viscUnit}{row.viscTemp != null ? ` @ ${row.viscTemp}°C` : ""}</>
                        : "—"}
                    </FailCell>
                    <FailCell verdict={phV}>
                      {row.ph != null
                        ? <>{row.ph}{row.phTemp != null ? ` @ ${row.phTemp}°C` : ""}</>
                        : "—"}
                    </FailCell>
                    <FailCell verdict={denV}>
                      {row.density != null
                        ? <>{row.density}{row.densityTemp != null ? ` @ ${row.densityTemp}°C` : ""}</>
                        : "—"}
                    </FailCell>
                    <FailCell verdict={scV}>
                      {row.solidPct != null ? `${row.solidPct}%` : "—"}
                    </FailCell>
                    <td style={{ maxWidth:180, color:"#555" }}>{row.remarks || "—"}</td>
                    <td><VerdictBadge verdict={verdict} /></td>
                    {canDelete && (
                      <td>
                        <button className="btn-small btn-danger" onClick={() => handleDelete(row.id)}>Del</button>
                      </td>
                    )}
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={colCount} style={{ textAlign:"center", color:"#aaa", padding:24 }}>
                    No records found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {modal && (
        <div className="overlay" onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <DailyAnalysisModal
            products={allProducts}
            onSave={handleCreate}
            onCancel={() => setModal(false)}
          />
        </div>
      )}
    </>
  );
}
